import { GeoJsonLayer, IconLayer } from '@deck.gl/layers';
import { LineString, Position } from 'geojson';
import { useMemo } from 'react';
import { useRouteDirections } from '@sections/simulation/use-route-directions';

interface UseDeckStreetRouteProps {
  coordinates: Position[] | null;
  accessToken: string | null;
  routeKey: string | null; // ej. route.id
}

export const useDeckStreetRoute = ({
  coordinates,
  accessToken,
  routeKey,
}: UseDeckStreetRouteProps) => {
  const { feature, loading, error } = useRouteDirections(
    coordinates,
    accessToken,
    routeKey,
  );

  const layers = useMemo(() => {
    if (!feature) return [];

    const baseLayers: any[] = [];

    // 1) Capa de la línea
    const lineLayer = new GeoJsonLayer({
      id: `street-route-layer-${routeKey ?? 'unknown'}`,
      data: feature,
      pickable: false,
      stroked: true,
      filled: false,
      lineWidthMinPixels: 2,
      getLineColor: [0, 0, 0, 200],
      getLineWidth: 1.5,
    });

    baseLayers.push(lineLayer);

    // 2) Capa de flecha (IconLayer)
    const geometry = feature.geometry as LineString;
    const coords = geometry.coordinates;

    if (coords.length >= 2) {
      // Generamos una flecha para cada segmento p[i-1] -> p[i]
      const STEP = 4;
      const arrowData: Array<{
        position: Position;
        angle: number;
      }> = [];
      for (let i = 1; i < coords.length; i += STEP) {
        const p1 = coords[i - 1];
        const p2 = coords[i];

        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];

        const angleRad = Math.atan2(dy, dx);
        const angleDeg = (angleRad * 180) / Math.PI;

        // Posición de la flecha:
        // - p2: flecha "llegando" al punto i
        // - punto medio: flecha en el centro del segmento
        const position: Position = p2;
        // const position: Position = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];

        arrowData.push({
          position,
          angle: angleDeg,
        });
      }
      const ARROW_ICON_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        `<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <polygon points="10,5 30,20 10,35" fill="black" />
        </svg>`,
      )}`;

      const arrowLayer = new IconLayer({
        id: `street-route-arrow-${routeKey ?? 'unknown'}`,
        data: arrowData,
        getPosition: (d: any) => d.position,
        getIcon: () => ({
          url: ARROW_ICON_URL,
          width: 40,
          height: 40,
          mask: false,
        }),
        getSize: 24,
        // rotamos el ícono según el ángulo calculado
        getAngle: (d: any) => d.angle,
        sizeUnits: 'pixels',
        billboard: true,
      });

      baseLayers.push(arrowLayer);
    }

    return baseLayers;
  }, [feature, routeKey]);

  return { layers, loading, error };
};
